import { ChangeDetectionStrategy, Component, computed, input, viewChildren, ViewEncapsulation } from "@angular/core";
import { FormField, FieldTree } from "@angular/forms/signals";
import { Layouts } from "./se.layouts";
import { S25SimpleCollapseModule } from "../../s25-simple-collapse/s25.simple.collapse.module";
import { FormsModule } from "@angular/forms";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { S25IconDropdownComponent } from "../../s25-icon/s25.icon.dropdown.component";
import { S25InfoMessageModule } from "../../s25-info-message/s25.info.message.module";
import { Nullable } from "../../../pojo/Util";
import { S25CheckboxModule } from "../../s25-checkbox/s25.checkbox.module";
import { S25Util } from "../../../util/s25-util";
import { S25Icons } from "../../s25-icon/s25.icon.const";
import { CssRuleComponent } from "../../s25-styles/s25.css.rule.component";
import { S25SimpleCollapseComponent } from "../../s25-simple-collapse/s25.simple.collapse.component";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { s25Resource } from "../../../util/s25.resource";
import { SearchCriteriaService } from "../../../services/search/search-criteria/search.criteria.service";
import { S25LoadingInlineStaticComponent } from "../../s25-loading/s25.loading.inline.static.component";

@Component({
    standalone: true,
    selector: "s25-ng-se-layout-config-setting",
    template: `
        @switch (meta().type) {
            @case ("fields") {
                @if (showLabel() && !!meta().label) {
                    <div class="divider-label">
                        {{ meta().label }}
                        @if (meta().info) {
                            <s25-ng-info-message [isPopover]="true">{{ meta().info }}</s25-ng-info-message>
                        }
                    </div>
                }
                <div class="fields">
                    @for (child of meta().settings; track child.id) {
                        <s25-ng-se-layout-config-setting
                            [meta]="child"
                            [settings]="settings()"
                            [readonly]="readonly()"
                        />
                    }
                </div>
            }
            @case ("sortable-collapses") {
                <div class="divider-label">
                    <div class="label">
                        @if (showLabel() && !!meta().label) {
                            {{ meta().label }}
                        }
                    </div>
                    <s25-ng-button
                        [type]="'outline'"
                        (clicked)="expandOrCollapseAll()"
                        [disabled]="!sortedSettings().length"
                    >
                        {{ allCollapsed() ? "Expand" : "Collapse" }} All
                    </s25-ng-button>
                </div>
                <div class="collapses">
                    @for (child of sortedSettings(); track child.id) {
                        <ng-template #collapseHeader>
                            <div class="collapse-header">
                                <div class="collapse-header--label">
                                    <span>{{ child.label ?? "Setting" }}</span>
                                    @if (child.info) {
                                        <s25-ng-info-message [isPopover]="true" (click)="$event.stopPropagation()">{{
                                            child.info
                                        }}</s25-ng-info-message>
                                    }
                                </div>
                                @if (!readonly()) {
                                    <div class="buttons icon-buttons">
                                        <s25-ng-button
                                            [type]="'outline'"
                                            [disabled]="$first"
                                            (clicked)="moveChild(child, -1)"
                                            [ariaLabel]="'Move up'"
                                        >
                                            <s25-ng-icon [type]="'arrowUp'" [ariaLabel]="false" />
                                        </s25-ng-button>
                                        <s25-ng-button
                                            [type]="'outline'"
                                            [disabled]="$last"
                                            (clicked)="moveChild(child, 1)"
                                            [ariaLabel]="'Move down'"
                                        >
                                            <s25-ng-icon [type]="'arrowDown'" [ariaLabel]="false" />
                                        </s25-ng-button>
                                    </div>
                                }
                            </div>
                        </ng-template>
                        <s25-simple-collapse
                            class="settings"
                            [header]="collapseHeader"
                            [buttonsSide]="'left'"
                            [expanded]="false"
                        >
                            <s25-ng-se-layout-config-setting
                                class="collapse-content"
                                [meta]="child"
                                [settings]="settings()"
                                [showLabel]="false"
                                [readonly]="readonly()"
                            />
                        </s25-simple-collapse>
                    }
                </div>
            }
            @case ("field") {
                @if (field()) {
                    @let value = field().value().value();
                    @let fieldLabel = meta().label ?? "Field";
                    @let controlId = "layout-config--setting-" + meta().id;
                    <div class="form-row field">
                        <label
                            [attr.for]="labelledControlTypes.has(meta().fieldType) ? controlId : null"
                            [attr.id]="controlId + '-label'"
                        >
                            {{ fieldLabel }}
                            @if (meta().info) {
                                <s25-ng-info-message [isPopover]="true">{{ meta().info }}</s25-ng-info-message>
                            }
                        </label>
                        @switch (meta().fieldType) {
                            @case ("icon") {
                                @if (readonly()) {
                                    <div>
                                        @if (value) {
                                            <s25-ng-icon [type]="asIconType(value)" [ariaLabel]="false" />
                                        }
                                        <span>{{ !value ? "None" : S25Icons.untyped[asIconType(value)].title }}</span>
                                    </div>
                                } @else {
                                    <s25-ng-icon-dropdown
                                        [ariaLabel]="fieldLabel"
                                        [value]="value ? { itemId: value } : null"
                                        (valueChange)="setValue($event?.itemId)"
                                        [hasNoneItem]="true"
                                        [noneItemLabel]="'No Icon'"
                                    />
                                }
                            }
                            @case ("text") {
                                @if (readonly()) {
                                    <p>{{ field().value().value() }}</p>
                                } @else {
                                    <input type="text" [id]="controlId" [formField]="field().value" />
                                }
                            }
                            @case ("textarea") {
                                @if (readonly()) {
                                    <pre class="field-readonly-block">{{ value }}</pre>
                                } @else {
                                    <textarea [id]="controlId" [formField]="field().value"></textarea>
                                }
                            }
                            @case ("checkbox") {
                                @if (readonly()) {
                                    <p>{{ field().value().value() ? "Yes" : "No" }}</p>
                                } @else {
                                    <s25-ng-checkbox
                                        [ariaLabel]="fieldLabel"
                                        [value]="value === 'true'"
                                        (valueChange)="setValue($event)"
                                    />
                                }
                            }
                            @case ("css") {
                                <s25-ng-css-rule
                                    role="group"
                                    [attr.aria-labelledby]="controlId + '-label'"
                                    [styles]="field().value().value()"
                                    (stylesChange)="setValue($event)"
                                    [allowedProperties]="meta().options?.cssProps ?? []"
                                    [readonly]="readonly()"
                                />
                            }
                            @case ("dropdown-multiselect") {
                                @if (readonly()) {
                                    <p>{{ field().value().value() ? "Yes" : "No" }}</p>
                                } @else {
                                    <s25-ng-generic-multiselect-dropdown
                                        [placeholder]="fieldLabel"
                                        [items]="meta().options?.dropdownOptions ?? []"
                                        [value]="
                                            parseMultiselectDropdown(
                                                field().value().value(),
                                                meta().options?.dropdownOptions
                                            )
                                        "
                                        (valueChange)="setValue(stringifyMultiselectDropdown($event))"
                                    />
                                }
                            }
                            @case ("single-select") {
                                @if (readonly()) {
                                    <p>{{ singleSelectLabel() }}</p>
                                } @else {
                                    <select
                                        [id]="controlId"
                                        class="cn-form__control"
                                        [ngModel]="value"
                                        (ngModelChange)="setValue($event)"
                                        [name]="controlId"
                                    >
                                        @for (option of meta().options?.dropdownOptions ?? []; track option.itemId) {
                                            <option [value]="option.itemId">{{ option.itemName }}</option>
                                        }
                                    </select>
                                }
                            }
                            @case ("attribute-picker") {
                                @if (orgAttributes.isLoading()) {
                                    <s25-ng-loading-inline-static />
                                } @else if (orgAttributes.error()) {
                                    <p class="field-error">Couldn't load organization custom attributes.</p>
                                } @else if (readonly()) {
                                    <p>{{ selectedAttributeNames() || "None selected" }}</p>
                                } @else if (!attributeOptions().length) {
                                    <p class="field-hint">
                                        No URL custom attributes exist for organizations. Create one in custom attribute
                                        admin.
                                    </p>
                                } @else {
                                    <s25-ng-generic-multiselect-dropdown
                                        [placeholder]="fieldLabel"
                                        [items]="attributeOptions()"
                                        [value]="parseMultiselectDropdown(value, attributeOptions())"
                                        (valueChange)="setValue(stringifyMultiselectDropdown($event))"
                                    />
                                }
                            }
                        }
                        @if (!readonly()) {
                            @for (error of field()().errorSummary(); track $index) {
                                <p class="field-error">{{ error.message }}</p>
                            }
                        }
                    </div>
                }
            }
        }
    `,
    styles: `
        .collapses {
            display: grid;
            gap: 1rem;
        }

        .collapse-header {
            font-size: 1em;
        }

        .collapse-header--label {
            display: flex;
            gap: 0.5rem;
        }

        .fields {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(20em, 1fr));
            gap: 1rem;
        }

        .field label {
            font-size: 0.8rem;
        }

        .field-readonly-block {
            margin: 0;
            white-space: pre-wrap;
            word-break: break-word;
            font-family: var(--font--pre), monospace;
            font-size: 0.8rem;
        }

        .field-hint {
            font-size: 0.75rem;
            color: var(--color--text-muted);
            font-style: italic;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25SimpleCollapseModule,
        S25ButtonComponent,
        S25IconComponent,
        S25IconDropdownComponent,
        FormField,
        S25InfoMessageModule,
        S25CheckboxModule,
        CssRuleComponent,
        S25DropdownPaginatedModule,
        FormsModule,
        S25LoadingInlineStaticComponent,
    ],
})
export class SeLayoutConfigSettingComponent {
    readonly meta = input.required<Layouts.WidgetStaticSetting>();
    readonly settings = input.required<FieldTree<Layouts.Widget["settings"]>>();
    readonly showLabel = input(true);
    readonly readonly = input(false);

    readonly collapses = viewChildren(S25SimpleCollapseComponent);
    readonly allCollapsed = computed(() => !!this.collapses()?.every((collapse) => !collapse.expanded()));

    protected readonly S25Icons = S25Icons;
    protected readonly labelledControlTypes = new Set<Layouts.WidgetSettingFieldType | undefined>([
        "text",
        "textarea",
        "single-select",
    ]);

    readonly field = computed(() => this.settings()[this.meta().id]);

    readonly orgAttributes = s25Resource({
        if: () => this.meta().fieldType === "attribute-picker",
        loader: () => SearchCriteriaService.getOrganizationCustomAttributes(),
    });

    readonly attributeOptions = computed((): DropDownItem<string>[] => {
        const allowedTypes = this.meta().options?.attributeTypes;
        return (this.orgAttributes.value() ?? [])
            .filter((attribute) => !allowedTypes?.length || allowedTypes.includes(String(attribute.itemTypeId)))
            .map((attribute) => ({ itemId: String(attribute.itemId), itemName: attribute.itemName }));
    });

    readonly selectedAttributeNames = computed(() => {
        if (this.meta().fieldType !== "attribute-picker") return "";
        return this.parseMultiselectDropdown(this.field()?.value().value(), this.attributeOptions())
            .map((item) => item.itemName)
            .join(", ");
    });

    readonly singleSelectLabel = computed(() => {
        const value = this.field().value().value();
        if (!value) return "";
        const options = this.meta().options?.dropdownOptions ?? [];
        return options.find((option) => String(option.itemId) === value)?.itemName ?? value;
    });

    readonly sortedSettings = computed(() => {
        const meta = this.meta();
        if (meta.type !== "sortable-collapses" || !meta.settings) return meta.settings;

        const settings = this.settings();
        const sorted = meta.settings.slice();
        sorted.sort((a, b) => {
            const A = settings[a.id]?.().value().order ?? 0;
            const B = settings[b.id]?.().value().order ?? 0;
            if (A === B) return 0;
            if (A === undefined) return -1;
            if (B === undefined) return 1;
            return A < B ? -1 : 1;
        });
        return sorted;
    });

    moveChild(child: Layouts.WidgetStaticSetting, dir: -1 | 1) {
        const children = this.meta().settings!.map((s) => this.settings()[s.id]);
        children.sort((a, b) => a.order().value() - b.order().value());
        const current = this.settings()[child.id].order().value();
        [children[current], children[current + dir]] = [children[current + dir], children[current]];
        children[current].order().markAsDirty();
        children[current + dir].order().markAsDirty();
        children.forEach((child, i) => child.order().value.set(i));
    }

    setValue(value: Nullable<any>) {
        if (S25Util.isDefined(value)) value = value.toString();
        this.field()
            .value()
            .value.set(value ?? "");
        this.field().value().markAsDirty();
    }

    asIconType(icon: string): S25Icons.Name {
        return <S25Icons.Name>icon;
    }

    expandOrCollapseAll() {
        const allCollapsed = this.allCollapsed();
        for (const collapse of this.collapses() ?? []) {
            if (allCollapsed) collapse.expand();
            else collapse.collapse();
        }
    }

    parseMultiselectDropdown(value: string | undefined, options: DropDownItem<string>[] | undefined): DropDownItem[] {
        if (!value || !options) return [];
        const names = new Map(options.map((option) => [option.itemId, option.itemName]));
        const values = S25Util.array
            .unique(value.split(","))
            .map((value) => ({ itemId: value, itemName: names.get(value) ?? value }));
        return values;
    }

    stringifyMultiselectDropdown(value: DropDownItem[]): string {
        return value.map((item) => item.itemId).join(",");
    }
}
